import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB'); } catch { return iso; }
};

const MODULE_BADGE = {
  case: { bg: 'primary', icon: 'bi-journal-medical', label: 'Case study' },
  drill: { bg: 'info', icon: 'bi-calculator', label: 'Calc exercise' },
  'spot-error': { bg: 'warning', text: 'dark', icon: 'bi-exclamation-diamond', label: 'Spot-error' },
  outpatient: { bg: 'dark', icon: 'bi-prescription2', label: 'Outpatient' },
};

const FacilitatorReviewQueue = ({ token, onOpenSession, onBack }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');     // pending | all
  const [search, setSearch] = useState('');

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const result = await api.listReviewQueue(token, { includeReviewed: filter === 'all' });
      setSessions(result.sessions || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load the review queue.');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) =>
      String(s.studentName || '').toLowerCase().includes(q)
      || String(s.drillTitle || '').toLowerCase().includes(q));
  }, [sessions, search]);

  const pendingCount = sessions.filter((s) => !s.reviewed).length;

  return (
    <div className="student-page">
      <Container className="mt-4 mb-5">
        <FacilitatorPageHeader
          title="Lecturer review queue"
          description="Submissions flagged for review. The auto-grade is shown for context; add a written review and mark each one done."
          onBack={onBack}
        />

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <ButtonGroup size="sm">
            <Button
              variant={filter === 'pending' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilter('pending')}
            >
              Pending {filter === 'pending' && pendingCount > 0 ? <Badge bg="light" text="dark" className="ms-1">{pendingCount}</Badge> : null}
            </Button>
            <Button
              variant={filter === 'all' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilter('all')}
            >
              All flagged
            </Button>
          </ButtonGroup>
          <Form.Control
            size="sm"
            type="search"
            placeholder="Search by student or case study…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Button size="sm" variant="outline-secondary" onClick={refresh} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" /> Refresh
          </Button>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading ? (
          <div className="text-center mt-4"><Spinner animation="border" /></div>
        ) : visible.length === 0 ? (
          <Alert variant="info">
            {filter === 'pending'
              ? 'No submissions are awaiting review. Nice.'
              : 'No flagged submissions match your filter.'}
          </Alert>
        ) : (
          <Card>
            <Table hover responsive className="mb-0 align-middle review-queue-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Case study</th>
                  <th style={{ width: 110 }}>Mode</th>
                  <th style={{ width: 130 }}>Submitted</th>
                  <th style={{ width: 120 }}>Score</th>
                  <th style={{ width: 130 }}>Status</th>
                  <th style={{ width: 110 }} className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => {
                  const badge = MODULE_BADGE[s.moduleType] || { bg: 'secondary', label: s.moduleType };
                  return (
                    <tr key={`${s.moduleType}-${s.sessionId}`}>
                      <td>
                        <div className="fw-semibold">{s.studentName}</div>
                        {s.studentEmail ? <div className="small text-muted">{s.studentEmail}</div> : null}
                      </td>
                      <td>{s.drillTitle || <em className="text-muted">unknown</em>}</td>
                      <td>
                        <Badge bg={badge.bg} text={badge.text}>
                          <i className={`bi ${badge.icon} me-1`} />{badge.label}
                        </Badge>
                        {s.moduleType === 'outpatient' && s.drillMode === 'verify'
                          ? <Badge bg="info" className="ms-1">Verify</Badge>
                          : s.moduleType === 'outpatient' && s.drillMode === 'prescribe'
                            ? <Badge bg="primary" className="ms-1">Prescribe</Badge>
                            : null}
                      </td>
                      <td className="small text-muted">{formatDate(s.completedAt)}</td>
                      <td>
                        {s.scoreOverride != null ? (
                          <>
                            <strong>{`${Math.round(s.effectiveScorePct ?? s.scoreOverride)}%`}</strong>
                            <Badge bg="info" className="ms-1" title="Lecturer adjusted">adj</Badge>
                            <div className="small text-muted">
                              auto {s.overallPct == null ? '—' : `${Math.round(s.overallPct)}%`}
                            </div>
                          </>
                        ) : (
                          <strong>{s.overallPct == null ? '—' : `${Math.round(s.overallPct)}%`}</strong>
                        )}
                      </td>
                      <td>
                        {s.reviewed ? (
                          <Badge bg="success" title={s.reviewerName || ''}>
                            <i className="bi bi-check-circle me-1" />Reviewed
                          </Badge>
                        ) : (
                          <Badge bg="warning" text="dark">
                            <i className="bi bi-hourglass-split me-1" />Pending
                          </Badge>
                        )}
                      </td>
                      <td className="text-end">
                        <Button size="sm" variant="primary" onClick={() => onOpenSession?.(s.moduleType, s.sessionId)}>
                          {s.reviewed ? 'View' : 'Review'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default FacilitatorReviewQueue;
